/**
 * WeekSetupWizard - Assistant de configuration de semaine
 * Basé sur agenda-semaine.html
 *
 * Étapes:
 * 1. Choix du rythme (minimal, équilibré, structuré, intense)
 * 2. Templates rapides à ajouter
 * 3. Horaires fixes (réveil, repas, coucher)
 * 4. Récap et confirmation
 */

import React, { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Check, X, Calendar, Play
} from 'lucide-react';
import { haptic } from '../../shared/utils/haptics';

// Couleurs (identiques à l'HTML)
const COLORS = {
  blue: { hex: '#4361EE', bg: 'rgba(67,97,238,0.1)', name: 'Bleu' },
  green: { hex: '#10B981', bg: 'rgba(16,185,129,0.1)', name: 'Vert' },
  red: { hex: '#EF4444', bg: 'rgba(239,68,68,0.1)', name: 'Rouge' },
  purple: { hex: '#8B5CF6', bg: 'rgba(139,92,246,0.1)', name: 'Violet' },
  orange: { hex: '#F59E0B', bg: 'rgba(245,158,11,0.1)', name: 'Orange' },
  pink: { hex: '#EC4899', bg: 'rgba(236,72,153,0.1)', name: 'Rose' },
  cyan: { hex: '#06B6D4', bg: 'rgba(6,182,212,0.1)', name: 'Cyan' },
};

// Templates par défaut (identiques à l'HTML)
const DEFAULT_TEMPLATES = [
  { id: 'standup', name: 'Stand-up', duration: 15, color: 'cyan', cat: 'meeting', icon: '⚡', desc: 'Point rapide équipe' },
  { id: 'reunion', name: 'Réunion d\'équipe', duration: 60, color: 'purple', cat: 'meeting', icon: '👥', desc: 'Réunion hebdomadaire' },
  { id: 'call', name: 'Appel client', duration: 30, color: 'green', cat: 'client', icon: '📞', desc: 'Appel téléphonique' },
  { id: 'focus', name: 'Focus / Deep work', duration: 120, color: 'blue', cat: 'work', icon: '🎯', desc: 'Travail concentré' },
  { id: 'lunch', name: 'Pause déjeuner', duration: 60, color: 'orange', cat: 'personal', icon: '🍽️', desc: 'Repas midi' },
  { id: 'code', name: 'Revue de code', duration: 45, color: 'purple', cat: 'work', icon: '💻', desc: 'Code review' },
  { id: 'sport', name: 'Sport', duration: 60, color: 'red', cat: 'health', icon: '🏃', desc: 'Activité physique' },
  { id: 'admin', name: 'Admin', duration: 30, color: 'cyan', cat: 'admin', icon: '📋', desc: 'Tâches administratives' },
];

// Routines essentielles TDAH
const ESSENTIAL_ROUTINES = [
  { id: 'wake', name: 'Réveil + Routine matinale', defaultTime: '07:00', duration: 45, color: 'orange', cat: 'personal', icon: '🌅', required: true },
  { id: 'meds', name: 'Médicaments', defaultTime: '07:30', duration: 5, color: 'red', cat: 'health', icon: '💊', required: true },
  { id: 'breakfast', name: 'Petit-déjeuner', defaultTime: '07:45', duration: 20, color: 'orange', cat: 'personal', icon: '🥐', required: false },
  { id: 'lunch', name: 'Déjeuner', defaultTime: '12:30', duration: 45, color: 'orange', cat: 'personal', icon: '🍽️', required: true },
  { id: 'break_pm', name: 'Pause après-midi', defaultTime: '15:30', duration: 15, color: 'orange', cat: 'personal', icon: '☕', required: false },
  { id: 'dinner', name: 'Dîner', defaultTime: '19:30', duration: 45, color: 'orange', cat: 'personal', icon: '🍝', required: true },
  { id: 'wind_down', name: 'Routine du soir', defaultTime: '21:30', duration: 30, color: 'purple', cat: 'personal', icon: '🌙', required: false },
  { id: 'sleep', name: 'Coucher', defaultTime: '22:30', duration: 15, color: 'purple', cat: 'personal', icon: '😴', required: true },
];

// Profils de rythme
const RHYTHM_PROFILES = [
  {
    id: 'minimal',
    name: 'Minimal',
    icon: '🌱',
    desc: 'Juste l\'essentiel : repas et routines vitales',
    color: 'green',
    includesEssentials: ['wake', 'meds', 'lunch', 'dinner', 'sleep'],
    workBlocks: 0,
  },
  {
    id: 'balanced',
    name: 'Équilibré',
    icon: '⚖️',
    desc: 'Routines + quelques blocs de travail',
    color: 'blue',
    includesEssentials: ['wake', 'meds', 'breakfast', 'lunch', 'dinner', 'sleep'],
    workBlocks: 2,
  },
  {
    id: 'structured',
    name: 'Structuré',
    icon: '📋',
    desc: 'Journées bien organisées avec pauses',
    color: 'purple',
    includesEssentials: ['wake', 'meds', 'breakfast', 'lunch', 'break_pm', 'dinner', 'wind_down', 'sleep'],
    workBlocks: 3,
  },
  {
    id: 'intense',
    name: 'Intense',
    icon: '🚀',
    desc: 'Planning complet et productif',
    color: 'orange',
    includesEssentials: ['wake', 'meds', 'breakfast', 'lunch', 'break_pm', 'dinner', 'wind_down', 'sleep'],
    workBlocks: 4,
  },
];

// Jours de la semaine
const DAYS = [
  { id: 0, short: 'Dim', full: 'Dimanche', isWeekend: true },
  { id: 1, short: 'Lun', full: 'Lundi', isWeekend: false },
  { id: 2, short: 'Mar', full: 'Mardi', isWeekend: false },
  { id: 3, short: 'Mer', full: 'Mercredi', isWeekend: false },
  { id: 4, short: 'Jeu', full: 'Jeudi', isWeekend: false },
  { id: 5, short: 'Ven', full: 'Vendredi', isWeekend: false },
  { id: 6, short: 'Sam', full: 'Samedi', isWeekend: true },
];

/**
 * Composant principal du wizard
 */
export const WeekSetupWizard = memo(function WeekSetupWizard({
  isOpen,
  onClose,
  onComplete,
}) {
  const [step, setStep] = useState(0);
  const [selectedRhythm, setSelectedRhythm] = useState('balanced');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [routineTimes, setRoutineTimes] = useState(() => {
    const times = {};
    ESSENTIAL_ROUTINES.forEach(r => {
      times[r.id] = r.defaultTime;
    });
    return times;
  });
  const [workDays, setWorkDays] = useState([1, 2, 3, 4, 5]); // Lun-Ven par défaut
  const [includeWeekends, setIncludeWeekends] = useState(false);

  const steps = ['Rythme', 'Routines', 'Activités', 'Récap'];

  // Génération des événements finaux
  const generateEvents = useCallback(() => {
    const events = [];
    const rhythm = RHYTHM_PROFILES.find(r => r.id === selectedRhythm);
    const today = new Date();
    const monday = getMonday(today);

    // Fonction pour obtenir la date d'un jour de la semaine
    const getDateForDay = (dayIndex) => {
      const date = new Date(monday);
      date.setDate(date.getDate() + dayIndex - 1); // dayIndex 1 = lundi
      return formatDate(date);
    };

    // 1. Ajouter les routines essentielles
    const essentialIds = rhythm?.includesEssentials || [];
    essentialIds.forEach(routineId => {
      const routine = ESSENTIAL_ROUTINES.find(r => r.id === routineId);
      if (!routine) return;

      const time = routineTimes[routineId] || routine.defaultTime;
      const endTime = addMinutes(time, routine.duration);

      // Ajouter pour chaque jour de travail (ou tous les jours selon config)
      const daysToAdd = includeWeekends ? [0, 1, 2, 3, 4, 5, 6] : workDays;
      daysToAdd.forEach(dayIndex => {
        events.push({
          id: `routine-${routineId}-${dayIndex}-${Date.now()}`,
          title: routine.name,
          date: getDateForDay(dayIndex === 0 ? 7 : dayIndex), // Dimanche = 7
          start: time,
          end: endTime,
          color: routine.color,
          cat: routine.cat,
          notes: routine.desc || '',
          isRoutine: true,
          recurrence: 'weekly',
        });
      });
    });

    // 2. Ajouter les templates sélectionnés (une fois, aujourd'hui)
    selectedTemplates.forEach(templateId => {
      const template = DEFAULT_TEMPLATES.find(t => t.id === templateId);
      if (!template) return;

      // Trouver un créneau libre aujourd'hui à 9h
      const baseTime = '09:00';
      const endTime = addMinutes(baseTime, template.duration);

      events.push({
        id: `template-${templateId}-${Date.now()}`,
        title: template.name,
        date: getDateForDay(today.getDay() === 0 ? 7 : today.getDay()),
        start: baseTime,
        end: endTime,
        color: template.color,
        cat: template.cat,
        notes: template.desc || '',
        isTemplate: true,
      });
    });

    // 3. Ajouter des blocs de travail selon le rythme
    const workBlockCount = rhythm?.workBlocks || 0;
    const workBlockTimes = ['09:00', '14:00', '16:00', '10:30'];

    workDays.forEach(dayIndex => {
      for (let i = 0; i < workBlockCount; i++) {
        const startTime = workBlockTimes[i] || '09:00';
        const duration = i === 0 ? 120 : 90; // Premier bloc plus long
        const endTime = addMinutes(startTime, duration);

        events.push({
          id: `work-block-${dayIndex}-${i}-${Date.now()}`,
          title: i === 0 ? 'Focus / Deep work' : 'Bloc de travail',
          date: getDateForDay(dayIndex),
          start: startTime,
          end: endTime,
          color: 'blue',
          cat: 'work',
          notes: 'Travail concentré',
          isWorkBlock: true,
        });
      }
    });

    return events;
  }, [selectedRhythm, selectedTemplates, routineTimes, workDays, includeWeekends]);

  const handleComplete = useCallback(() => {
    haptic.success();
    const events = generateEvents();
    onComplete?.(events);
    onClose?.();
  }, [generateEvents, onComplete, onClose]);

  const nextStep = useCallback(() => {
    haptic.tap();
    if (step < steps.length - 1) {
      setStep(s => s + 1);
    } else {
      handleComplete();
    }
  }, [step, steps.length, handleComplete]);

  const prevStep = useCallback(() => {
    haptic.tap();
    if (step > 0) setStep(s => s - 1);
  }, [step]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-lg mx-4 bg-zinc-900 rounded-3xl overflow-y-auto shadow-2xl max-h-[95svh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Configure ta semaine</h2>
              <p className="text-xs text-zinc-500">Étape {step + 1}/{steps.length}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-zinc-800">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
            animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto p-5">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <StepRhythm
                key="rhythm"
                selected={selectedRhythm}
                onSelect={setSelectedRhythm}
              />
            )}
            {step === 1 && (
              <StepRoutines
                key="routines"
                rhythm={selectedRhythm}
                routineTimes={routineTimes}
                onTimeChange={(id, time) => setRoutineTimes(prev => ({ ...prev, [id]: time }))}
                workDays={workDays}
                onWorkDaysChange={setWorkDays}
                includeWeekends={includeWeekends}
                onIncludeWeekendsChange={setIncludeWeekends}
              />
            )}
            {step === 2 && (
              <StepActivities
                key="activities"
                selected={selectedTemplates}
                onToggle={(id) => {
                  setSelectedTemplates(prev =>
                    prev.includes(id)
                      ? prev.filter(x => x !== id)
                      : [...prev, id]
                  );
                }}
              />
            )}
            {step === 3 && (
              <StepRecap
                key="recap"
                rhythm={selectedRhythm}
                routineTimes={routineTimes}
                selectedTemplates={selectedTemplates}
                workDays={workDays}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-zinc-800">
          {step > 0 && (
            <motion.button
              onClick={prevStep}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-zinc-800 hover:bg-zinc-700
                text-zinc-300 font-medium rounded-xl transition-colors"
              whileTap={{ scale: 0.98 }}
            >
              <ChevronLeft className="w-5 h-5" />
              Retour
            </motion.button>
          )}
          <motion.button
            onClick={nextStep}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3
              bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90
              text-white font-medium rounded-xl transition-opacity"
            whileTap={{ scale: 0.98 }}
          >
            {step === steps.length - 1 ? (
              <>
                <Play className="w-5 h-5" />
                C'est parti !
              </>
            ) : (
              <>
                Suivant
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
});

/**
 * Étape 1: Choix du rythme
 */
const StepRhythm = memo(function StepRhythm({ selected, onSelect }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">Quel est ton rythme ?</h3>
        <p className="text-sm text-zinc-500">
          Choisis selon ton énergie et tes besoins du moment
        </p>
      </div>

      <div className="grid gap-3">
        {RHYTHM_PROFILES.map((rhythm) => (
          <motion.button
            key={rhythm.id}
            onClick={() => {
              haptic.tap();
              onSelect(rhythm.id);
            }}
            className={`flex items-center gap-4 p-4 rounded-2xl text-left transition-all ${
              selected === rhythm.id
                ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-2 border-blue-500'
                : 'bg-zinc-800/50 border-2 border-transparent hover:border-zinc-700'
            }`}
            whileTap={{ scale: 0.98 }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${COLORS[rhythm.color]?.hex}20` }}
            >
              {rhythm.icon}
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-white">{rhythm.name}</p>
              <p className="text-sm text-zinc-400">{rhythm.desc}</p>
            </div>
            {selected === rhythm.id && (
              <Check className="w-6 h-6 text-blue-400" />
            )}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
});

/**
 * Étape 2: Configuration des routines
 */
const StepRoutines = memo(function StepRoutines({
  rhythm,
  routineTimes,
  onTimeChange,
  workDays,
  onWorkDaysChange,
  includeWeekends,
  onIncludeWeekendsChange,
}) {
  const rhythmProfile = RHYTHM_PROFILES.find(r => r.id === rhythm);
  const includedRoutines = ESSENTIAL_ROUTINES.filter(r =>
    rhythmProfile?.includesEssentials.includes(r.id)
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-white mb-2">Tes horaires fixes</h3>
        <p className="text-sm text-zinc-500">
          Ajuste selon ton rythme naturel
        </p>
      </div>

      {/* Sélecteur de jours */}
      <div className="space-y-3">
        <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          Jours de travail
        </label>
        <div className="flex gap-2 justify-center">
          {DAYS.filter(d => !d.isWeekend).map((day) => (
            <button
              key={day.id}
              onClick={() => {
                haptic.tap();
                onWorkDaysChange(prev =>
                  prev.includes(day.id)
                    ? prev.filter(d => d !== day.id)
                    : [...prev, day.id]
                );
              }}
              className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all ${
                workDays.includes(day.id)
                  ? 'bg-blue-500 text-white'
                  : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {day.short.charAt(0)}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl cursor-pointer">
          <input
            type="checkbox"
            checked={includeWeekends}
            onChange={(e) => onIncludeWeekendsChange(e.target.checked)}
            className="w-5 h-5 rounded bg-zinc-700 border-none text-blue-500 focus:ring-blue-500"
          />
          <span className="text-sm text-zinc-300">Inclure les routines le weekend</span>
        </label>
      </div>

      {/* Liste des routines */}
      <div className="space-y-2">
        {includedRoutines.map((routine) => (
          <div
            key={routine.id}
            className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl"
          >
            <span className="text-xl">{routine.icon}</span>
            <span className="flex-1 text-sm font-medium text-white">{routine.name}</span>
            <input
              type="time"
              value={routineTimes[routine.id] || routine.defaultTime}
              onChange={(e) => onTimeChange(routine.id, e.target.value)}
              className="bg-zinc-700 border-none rounded-lg px-3 py-2 text-sm text-white
                focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        ))}
      </div>
    </motion.div>
  );
});

/**
 * Étape 3: Choix des activités/templates
 */
const StepActivities = memo(function StepActivities({ selected, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-white mb-2">Activités typiques</h3>
        <p className="text-sm text-zinc-500">
          Sélectionne celles que tu fais souvent
        </p>
      </div>

      <div className="grid gap-2">
        {DEFAULT_TEMPLATES.map((template) => {
          const isSelected = selected.includes(template.id);
          return (
            <motion.button
              key={template.id}
              onClick={() => {
                haptic.tap();
                onToggle(template.id);
              }}
              className={`flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                isSelected
                  ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/50'
                  : 'bg-zinc-800/50 border border-transparent hover:border-zinc-700'
              }`}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-xl">{template.icon}</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{template.name}</p>
                <p className="text-xs text-zinc-500">{template.duration} min</p>
              </div>
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  isSelected ? 'bg-blue-500' : 'bg-zinc-700'
                }`}
              >
                {isSelected && <Check className="w-4 h-4 text-white" />}
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
});

/**
 * Étape 4: Récapitulatif
 */
const StepRecap = memo(function StepRecap({
  rhythm,
  routineTimes,
  selectedTemplates,
  workDays,
}) {
  const rhythmProfile = RHYTHM_PROFILES.find(r => r.id === rhythm);
  const includedRoutines = ESSENTIAL_ROUTINES.filter(r =>
    rhythmProfile?.includesEssentials.includes(r.id)
  );
  const templates = DEFAULT_TEMPLATES.filter(t => selectedTemplates.includes(t.id));

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-4">
        <div className="text-4xl mb-3">🎉</div>
        <h3 className="text-xl font-bold text-white mb-2">Ta semaine est prête !</h3>
        <p className="text-sm text-zinc-500">Voici ce qui va être créé</p>
      </div>

      {/* Rythme choisi */}
      <div className="p-4 bg-zinc-800/50 rounded-xl">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{rhythmProfile?.icon}</span>
          <div>
            <p className="text-sm font-semibold text-white">Mode {rhythmProfile?.name}</p>
            <p className="text-xs text-zinc-500">{rhythmProfile?.desc}</p>
          </div>
        </div>
      </div>

      {/* Résumé des routines */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          Routines quotidiennes ({includedRoutines.length})
        </h4>
        <div className="flex flex-wrap gap-2">
          {includedRoutines.map((r) => (
            <span
              key={r.id}
              className="px-3 py-1.5 bg-zinc-800 rounded-full text-xs text-zinc-300"
            >
              {r.icon} {r.name} · {routineTimes[r.id]}
            </span>
          ))}
        </div>
      </div>

      {/* Résumé des activités */}
      {templates.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
            Activités ajoutées ({templates.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <span
                key={t.id}
                className="px-3 py-1.5 bg-zinc-800 rounded-full text-xs text-zinc-300"
              >
                {t.icon} {t.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Jours de travail */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          Jours de travail
        </h4>
        <div className="flex gap-2">
          {DAYS.filter(d => !d.isWeekend).map((day) => (
            <span
              key={day.id}
              className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold ${
                workDays.includes(day.id)
                  ? 'bg-blue-500 text-white'
                  : 'bg-zinc-800 text-zinc-600'
              }`}
            >
              {day.short.charAt(0)}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
});

// Utilitaires
function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addMinutes(time, minutes) {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

export default WeekSetupWizard;
